import { debounce } from 'lodash-es'

import store from '@/store'
import { getBase64Id, removeFromArray } from '@/utils/general-utils'
import {
  addShopifyCartLines,
  Cart,
  createShopifyCart,
  getShopifyCart,
  removeShopifyCartLines,
  updateShopifyCartAttributes,
  updateShopifyCartLines,
} from './shopify'
import { CartLineInput, CartLineUpdateInput } from '@/types/storefront.types'

export async function createCheckoutCart(): Promise<Cart> {
  const cart = await createShopifyCart()
  const id = getBase64Id(cart.id)
  const merchant = store.state.auth.merchant

  localStorage.setItem(`vc_ticket_${merchant}_checkoutCartId`, id)

  return { ...cart, id }
}

export async function getCheckoutCart(id: string): Promise<Cart> {
  const cart = await getShopifyCart(id)

  if (!cart) {
    return createCheckoutCart()
  }

  return cart
}

export async function addLines(
  cartId: string,
  lines: CartLineInput[]
): Promise<Cart> {
  return await addShopifyCartLines(cartId, lines)
}

export async function updateLines(
  cartId: string,
  lines: CartLineUpdateInput[]
): Promise<Cart> {
  return await updateShopifyCartLines(cartId, lines)
}

export const updateLinesDebounced = debounce(
  async (
    cartId: string,
    lines: CartLineUpdateInput[],
    callback?: (cart: Cart) => void
  ) => {
    const cart = await updateLines(cartId, lines)

    if (callback) {
      callback(cart)
    }
  },
  500
)

export async function removeLines(
  cartId: string,
  lineIds: string[]
): Promise<Cart> {
  return await removeShopifyCartLines(cartId, lineIds)
}

export async function updateDate(
  cartId: string,
  newISODate: string,
  prevAttributes: Cart['attributes']
): Promise<Cart> {
  const attributes = removeFromArray(
    (attr) => attr.key === 'dateOfVisit',
    prevAttributes
  ).map((attr) => ({ key: attr.key, value: attr.value ?? '' }))

  return await updateShopifyCartAttributes(cartId, [
    ...attributes,
    { key: 'dateOfVisit', value: newISODate },
  ])
}
